import { useState, useEffect } from 'react';
import { Trans, t } from '@lingui/macro';
import styled from '@emotion/styled';
import { Select, TextInput, ChipsList, Text, IconButton, Avatar, DatePicker } from 'components';
import { FilterRow as FilterRowType, formatDate } from './transforms';
import { Number } from './Number';
import { DateFilter, DateProps, placeholder } from './DateFilter';
import type { TableFilter } from 'features/misc/table';
import { colors } from 'styles/theme/colors';
import { Trash } from '@phosphor-icons/react';
import { Box } from '@mui/material';
import { trans } from 'features/misc/table/utils';

export const commenProps = {
  autoFocus: true,
  sx: {
    '&&.MuiFormControl-root': {
      maxWidth: '320px',
      minHeight: '44px',
    },
    '&&.MuiAutocomplete-root': {
      maxWidth: '320px',
      minHeight: '44px',
    },
    '&&.MuiInputBase-root': {
      maxWidth: '320px',
      width: '100%',
      padding: '10px 14px',
      margin: 0,
      font: 'unset',
    },
  },
  inputVariant: 'outlined',
  variant: 'outlined',
};

const filterTypeConstant = {
  text: { text: '' },
  dd: { dd: [] },
  dateRange: { dateFrom: null, dateTo: null },
  date: null,
  number: { numberFrom: undefined, numberTo: undefined },
};

type FilterRowProps = {
  onRemove: () => void;
  data: FilterRowType;
  filterRows: FilterRowType[];
  filters: TableFilter[];
  selected: FilterRowType[];
  onChange: (data: FilterRowType, doUpdate?: boolean) => void;
  update: () => void;
};

export const FilterRow = ({
  onRemove,
  filterRows,
  data,
  filters,
  selected,
  onChange,
  update,
}: FilterRowProps) => {
  const [values, setValues] = useState<FilterRowType>(data);
  const column = values?.column || undefined;
  const filter = filters?.find(f => f.id === column) as TableFilter;

  const filterType = filter?.type as TableFilter['type'];
  const defaultValue = filterType ? filterTypeConstant[filterType] : {};

  useEffect(() => {
    onChange(values);
  }, []);

  const onUpdate = (vals: FilterRowType, doUpdate: boolean = false) => {
    setValues(prev => vals);
    if (doUpdate) {
      onChange(vals, doUpdate);
    }
  };

  return (
    <>
      <Row gap="24px">
        <Row overflow="hidden" style={{ overflowX: 'auto', paddingRight: '5px', flex: 1 }}>
          <Text variant="bodySm" weight="regular" right={16}>
            <Trans>Show a view, where</Trans>
          </Text>
          <Select
            placeholder={t`Select column`}
            sx={{
              width: '100%',
              ...(filterType === 'dateRange' || filterType === 'number'
                ? { maxWidth: '320px' }
                : { minWidth: '320px', maxWidth: '320px' }),
            }}
            MenuProps={{
              sx: { marginTop: '10px' },
            }}
            variant="outlined"
            options={filters
              .filter(
                item => !selected.some(select => select?.column === item?.id && item?.id !== column)
              )
              .map(f => ({ id: f.id, label: trans(f.label) as string }))}
            value={column}
            onChange={(e, val) => {
              onUpdate(
                { ...values, column: e.target.value as string, ...(defaultValue ?? {}) },
                true
              );
            }}
          />
          {filterType && (
            <Text variant="bodySm" weight="regular" right={16} left={24}>
              <Trans>contains</Trans>
            </Text>
          )}
          {filterType === 'text' ? (
            <TextInput
              {...commenProps}
              placeholder={t`Enter your text here`}
              variant="outlined"
              fullWidth
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  onUpdate({ ...values }, true);
                }
              }}
              value={values?.text}
              onChange={e => onUpdate({ ...values, text: e.target.value })}
              onBlur={() => onUpdate({ ...values }, true)}
            />
          ) : null}
          {filterType === 'dateRange' ? (
            <DateFilter
              {...(commenProps as Omit<DateProps, 'onChange'>)}
              isStopPropagationOnKeyDown
              from={values?.dateFrom}
              to={values?.dateTo}
              onChange={(from, to) => {
                onUpdate({ ...values, dateFrom: from, dateTo: to }, true);
              }}
            />
          ) : null}
          {filterType === 'date' ? (
            <DatePicker
              {...(commenProps as Omit<DateProps, 'onChange'>)}
              id="filter-date"
              placeholder={placeholder}
              initialDate={formatDate(values?.date)}
              isStopPropagationOnKeyDown
              onBlur={date => {
                onUpdate({ ...values, date: date?.toDate() }, true);
              }}
            />
          ) : null}
          {filterType === 'number' ? (
            <Number
              {...commenProps}
              from={values?.numberFrom}
              to={values?.numberTo}
              onChange={(from, to) => onUpdate({ ...values, numberFrom: from, numberTo: to })}
              onBlur={() => onUpdate({ ...values }, true)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  onUpdate({ ...values }, true);
                }
              }}
            />
          ) : null}
          {filterType === 'dd' && filter?.component
            ? filter.component(values?.dd, val => {
                if (!val) {
                  return;
                }
                onUpdate({ ...values, dd: val ? [...val] : [] }, true);
              })
            : null}
        </Row>
        {(filterRows.length !== 1 || filterType) && (
          <IconButton onClick={() => onRemove()} size="md" sx={{ marginLeft: 'auto' }}>
            <Trash size={20} />
          </IconButton>
        )}
      </Row>
      {values?.dd?.length ? (
        <ChipsList
          chips={values?.dd.map(s => ({
            size: 'md',
            isTooltip: false,
            label: s.label,
            ...(filter?.showChipAvatar
              ? {
                  avatar: (
                    <Avatar
                      letters={s.label}
                      size="xs"
                      color={s.customProps?.color ? `#${s.customProps.color}` : ''}
                    />
                  ),
                }
              : {}),
            renderLabel: label => (
              <Text variant="labelSm" color={colors.brand[700]} weight="semiBold">
                {label}
              </Text>
            ),
            id: `${s.id}`,
            isBordered: false,
            background: colors.brand[100],
            deleteIconColor: colors.brand[300],
          }))}
          api={filters.find(f => f.id === column)?.api}
          onDelete={chip => {
            onUpdate(
              {
                ...values,
                dd: values.dd.filter(oldVal => String(oldVal.id) !== String(chip.id || '')),
              },
              true
            );
          }}
        />
      ) : null}
    </>
  );
};

const Row = styled(Box)`
  display: flex;
  flex-direction: row;
  white-space: nowrap;
  align-items: center;
`;
